import React, { useState, useRef, useEffect } from 'react';

interface CollaborativeEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}

export const CollaborativeEditor: React.FC<CollaborativeEditorProps> = ({
  value,
  onChange,
  placeholder = "Start collaborating on your document...",
  className = ""
}) => {
  const [isReady, setIsReady] = useState(false);
  const editorRef = useRef<HTMLDivElement>(null);

  // Simulate collaborative features
  const [collaborators] = useState([
    { id: 1, name: 'You', color: '#1976d2', active: true },
    { id: 2, name: 'John Doe', color: '#388e3c', active: false },
    { id: 3, name: 'Jane Smith', color: '#f57c00', active: false }
  ]);

  const [documentStats, setDocumentStats] = useState({
    words: 0,
    characters: 0,
    lastSaved: new Date()
  });

  // Update document stats when content changes
  useEffect(() => {
    const text = value.replace(/<[^>]*>/g, '');
    setDocumentStats({
      words: text.split(/\s+/).filter(word => word.length > 0).length,
      characters: text.length,
      lastSaved: new Date()
    });
  }, [value]);

  // Set ready state
  useEffect(() => {
    setIsReady(true);
  }, []);

  const handleInput = (e: React.FormEvent<HTMLDivElement>) => {
    const newContent = e.currentTarget.innerHTML;
    onChange(newContent);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    // Handle basic formatting shortcuts
    if (e.ctrlKey || e.metaKey) {
      switch (e.key) {
        case 'b':
          e.preventDefault();
          document.execCommand('bold');
          break;
        case 'i':
          e.preventDefault();
          document.execCommand('italic');
          break;
        case 'u':
          e.preventDefault();
          document.execCommand('underline');
          break;
      }
    }
  };

  const formatText = (command: string, value?: string) => {
    document.execCommand(command, false, value);
    if (editorRef.current) {
      const newContent = editorRef.current.innerHTML;
      onChange(newContent);
    }
  };

  return (
    <div className={`collaborative-editor ${className}`}>
      {/* Collaboration Header */}
      <div className="collaboration-header bg-white border-b border-gray-200 p-4 rounded-t-lg">
        {/* Presence List */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium">Collaborators:</span>
            <div className="flex items-center gap-2">
              {collaborators.map((collaborator) => (
                <div
                  key={collaborator.id}
                  className={`flex items-center gap-2 px-2 py-1 rounded-full text-xs ${
                    collaborator.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: collaborator.color }}
                  ></div>
                  <span>{collaborator.name}</span>
                  {collaborator.active && (
                    <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                  )}
                </div>
              ))}
            </div>
          </div>
          
          {/* Document Stats */}
          <div className="flex items-center gap-4 text-xs text-gray-600">
            <span>{documentStats.words} words</span>
            <span>{documentStats.characters} characters</span>
            <span>Saved {documentStats.lastSaved.toLocaleTimeString()}</span>
          </div>
        </div>

        {/* Collaboration Actions */}
        <div className="flex gap-2">
          <button 
            className="px-3 py-1 bg-blue-500 text-white rounded-md text-sm hover:bg-blue-600 transition-colors"
            onClick={() => console.log('Share document')}
          >
            📤 Share
          </button>
          <button 
            className="px-3 py-1 bg-green-500 text-white rounded-md text-sm hover:bg-green-600 transition-colors"
            onClick={() => console.log('Add comment')}
          >
            💬 Comment
          </button>
          <button 
            className="px-3 py-1 bg-purple-500 text-white rounded-md text-sm hover:bg-purple-600 transition-colors"
            onClick={() => console.log('Version history')}
          >
            🕒 History
          </button>
          <button 
            className="px-3 py-1 bg-orange-500 text-white rounded-md text-sm hover:bg-orange-600 transition-colors"
            onClick={() => console.log('Suggest changes')}
          >
            ✏️ Suggest
          </button>
        </div>
      </div>

      {/* Toolbar */}
      <div className="border-b border-gray-200 bg-gray-50 p-2">
        <div className="flex items-center gap-2 flex-wrap">
          <button
            className="px-2 py-1 bg-white border border-gray-300 rounded text-sm hover:bg-gray-100"
            onClick={() => formatText('bold')}
          >
            <strong>B</strong>
          </button>
          <button
            className="px-2 py-1 bg-white border border-gray-300 rounded text-sm hover:bg-gray-100"
            onClick={() => formatText('italic')}
          >
            <em>I</em>
          </button>
          <button
            className="px-2 py-1 bg-white border border-gray-300 rounded text-sm hover:bg-gray-100"
            onClick={() => formatText('underline')}
          >
            <u>U</u>
          </button>
          <div className="w-px h-6 bg-gray-300"></div>
          <button
            className="px-2 py-1 bg-white border border-gray-300 rounded text-sm hover:bg-gray-100"
            onClick={() => formatText('insertUnorderedList')}
          >
            • List
          </button>
          <button
            className="px-2 py-1 bg-white border border-gray-300 rounded text-sm hover:bg-gray-100"
            onClick={() => formatText('insertOrderedList')}
          >
            1. List
          </button>
          <div className="w-px h-6 bg-gray-300"></div>
          <button
            className="px-2 py-1 bg-white border border-gray-300 rounded text-sm hover:bg-gray-100"
            onClick={() => formatText('justifyLeft')}
          >
            ← Left
          </button>
          <button
            className="px-2 py-1 bg-white border border-gray-300 rounded text-sm hover:bg-gray-100"
            onClick={() => formatText('justifyCenter')}
          >
            ↔ Center
          </button>
          <button
            className="px-2 py-1 bg-white border border-gray-300 rounded text-sm hover:bg-gray-100"
            onClick={() => formatText('justifyRight')}
          >
            → Right
          </button>
        </div>
      </div>

      {/* Editor Container */}
      <div className="bg-gray-100 p-6">
        <div className="w-[8.5in] h-[11in] mx-auto bg-white shadow-lg border border-gray-300" 
             style={{
               boxShadow: '0 4px 8px rgba(0,0,0,0.1), 0 6px 20px rgba(0,0,0,0.1)'
             }}>
          <div style={{ padding: '1in' }}>
            <div
              ref={editorRef}
              contentEditable
              suppressContentEditableWarning={true}
              className="w-full h-[9in] outline-none focus:outline-none overflow-auto"
              style={{
                fontFamily: 'Calibri, Arial, sans-serif',
                fontSize: '11pt',
                lineHeight: '1.15',
                color: '#000000'
              }}
              onInput={handleInput}
              onKeyDown={handleKeyDown}
              onFocus={() => console.log('Editor focused - collaboration session active')}
              onBlur={() => console.log('Editor blurred - auto-saving...')}
              dangerouslySetInnerHTML={{ __html: value || `<p style="color: #999; margin: 0;">${placeholder}</p>` }}
            />
          </div>
        </div>
      </div>

      {/* Status Bar */}
      <div className="bg-gray-200 border-t border-gray-300 px-4 py-2 text-xs text-gray-600 flex justify-between items-center rounded-b-lg">
        <div className="flex items-center gap-4">
          <span className={`flex items-center gap-1 ${isReady ? 'text-green-600' : 'text-orange-600'}`}>
            <span className={`w-2 h-2 rounded-full ${isReady ? 'bg-green-500' : 'bg-orange-500'} animate-pulse`}></span>
            {isReady ? 'Ready for collaboration' : 'Initializing...'}
          </span>
          <span>Page 1 of 1</span>
          <span>{documentStats.words} words</span>
          <span>{documentStats.characters} characters</span>
        </div>
        <div className="flex items-center gap-2">
          <span>Auto-save: ON</span>
          <span>100% zoom</span>
        </div>
      </div>
    </div>
  );
};